import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { qcpAuthHeaders } from '@/lib/qcp/auth';

/* ------------------------------------------------------------------ */
/* REST response types                                                 */
/*                                                                     */
/* Hand-mirrored from QChinesePoker/packages/qcp-db/src/types.ts.     */
/* Keep in sync with the server contract when shapes change.          */
/* ------------------------------------------------------------------ */

/** A single leaderboard row (`GET /leaderboard`). */
export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  elo: number;
  rank: number;
}

/** A summary of one played match (no secret data). */
export interface MatchSummary {
  matchId: string;
  players: Record<'A' | 'B', { userId: string; displayName: string }>;
  net: Record<'A' | 'B', number>;
  winnerSeat: 'A' | 'B' | null;
  reason: string;
  finishedAt: number; // epoch ms
}

/** One hand's fairness + scoring record (mirror of qcp-db HandRecord). */
export interface HandRecord {
  handNumber: number;
  netA: number;
  netB: number;
  commitHash: string;
  /** null on forfeited hands (clock-flag / resign): never dealt or completed. */
  serverSeed: string | null;
  deckOrder: Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }> | null;
  boards: Record<
    'A' | 'B',
    {
      front: Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }>;
      middle: Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }>;
      back: Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }>;
    }
  > | null;
  discards: Record<'A' | 'B', Array<{ r: number; s: 'c' | 'd' | 'h' | 's' }>> | null;
}

/**
 * Full multi-hand replay (`GET /matches/:id/replay`): summary + one
 * HandRecord per played hand (FL extensions included). Final boards only —
 * the server keeps no per-round action log (round-by-round reconstruction
 * is impossible client-side; see docs/HANDOFF-qcp-replay-actionlog.md).
 */
export interface MatchReplay extends MatchSummary {
  setLength: number;
  hands: HandRecord[];
}

/* ------------------------------------------------------------------ */
/* RTK Query client                                                    */
/* ------------------------------------------------------------------ */

/**
 * QChinesePoker REST API client. Talks to qcp-api at `:4100/*`.
 *
 * Auth: dev-bypass-first pattern (mirrors qclansApi.ts / qgammonApi.ts).
 *   • Dev: `X-Dev-Bypass` + optional `X-Dev-User` (from `env.qcpDevBypass` /
 *     `env.qcpDevUser`) when set — skips the Logto path entirely, letting the
 *     `/chinesepoker` surface work against the MacMini before the "Qlash API
 *     QChinesePoker" Logto resource is registered.
 *   • Prod: `Authorization: Bearer <logto>` minted for the QCP audience when
 *     the resource exists and is granted to the TMA app; null token → no
 *     header attached (server answers 401, surface self-hides).
 *
 * The actual header resolution lives in `@/lib/qcp/auth` (qcpAuthHeaders),
 * shared with the Socket.IO gameserver path — ONE canonical place for
 * QCP auth, used by both REST and the WS handshake hook.
 *
 * ⚠️  Golden rule: do NOT add the QCP resource to `src/lib/logto.ts`'s
 * `logtoConfig.resources` or the `buildAuthUrl` `resource` params until
 * "Qlash API QChinesePoker" is registered in Logto — an unregistered
 * resource in the authorize request returns `invalid_target` and breaks
 * ALL sign-in. Use `env.qcpDevBypass` for local testing until then.
 *
 * Dev: env.qcpApiUrl = '/qchinesepoker-api', Vite proxy → MacMini :4100.
 * Prod: env.qcpApiUrl = 'https://qchinesepoker.qlash.gg'.
 *
 * Note on card images: `GET /matches/:id/card.png` is a PUBLIC endpoint
 * that returns a PNG result card. It is NOT an RTK Query endpoint — result
 * screens build the URL directly from `env.qcpApiUrl` and use it as
 * `<img src>` without needing auth or caching.
 */
export const qchinesepokerApi = createApi({
  reducerPath: 'qchinesepokerApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qcpApiUrl,
      prepareHeaders: async (headers) => {
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        // Dev-bypass takes precedence in dev when configured — lets the surface
        // work before the Logto QCP resource is granted. qcpAuthHeaders() encodes
        // the full resolution order (Logto → dev-bypass → none) shared with the
        // Socket.IO gameserver handshake hook.
        const auth = await qcpAuthHeaders();
        for (const [k, v] of Object.entries(auth)) headers.set(k, v);
        return headers;
      },
    }),
    {
      // Same retry policy as qclans/qchess/qgammon: retry transient errors once.
      // 4xx are deterministic (not-found, unauthenticated) — a retry just delays
      // the error.
      retryCondition: (error, _args, { attempt }) => {
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  ),
  keepUnusedDataFor: 120,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Leaderboard', 'MyMatches', 'Match'],
  endpoints: (builder) => ({
    /**
     * ELO leaderboard (`GET /leaderboard?limit=`).
     * Public — does not require auth. Returns the top-N players by ELO.
     */
    getLeaderboard: builder.query<LeaderboardEntry[], { limit?: number }>({
      query: ({ limit = 50 } = {}) => ({
        url: '/leaderboard',
        params: { limit },
      }),
      providesTags: ['Leaderboard'],
    }),

    /**
     * A single match summary (`GET /matches/:id`).
     * Public — returns net scores, winner, and reason without secret data.
     * 404 when the match does not exist.
     */
    getMatch: builder.query<MatchSummary, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}`,
      providesTags: (_result, _error, matchId) => [{ type: 'Match', id: matchId }],
    }),

    /**
     * The authenticated caller's match history (`GET /matches/mine?limit=`).
     * Requires auth (Bearer or dev-bypass) — the API resolves the caller from
     * the JWT sub (or the `X-Dev-User` header in dev). Returns [] for a user
     * who has never finished a match.
     */
    getMyMatches: builder.query<MatchSummary[], { limit?: number }>({
      query: ({ limit = 20 } = {}) => ({
        url: '/matches/mine',
        params: { limit },
      }),
      providesTags: ['MyMatches'],
    }),

    /**
     * Full match replay (`GET /matches/:id/replay`).
     * Extends the summary with the server seed, deck order, boards, and
     * discards — all the data needed for provably-fair verification in the
     * result screen. 404 when the match is not found or was dissolved
     * before any cards were dealt (serverSeed is null in that case).
     */
    getReplay: builder.query<MatchReplay, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}/replay`,
    }),
  }),
});

export const {
  useGetLeaderboardQuery,
  useGetMatchQuery,
  useGetMyMatchesQuery,
  useGetReplayQuery,
} = qchinesepokerApi;
